import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, AlertCircle } from "lucide-react";
import { useLocalization } from "@/hooks/useLocalization";
import { buildAbsoluteUrl } from "@/lib/basePath";

interface PaymentRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentRequest: {
    id: string;
    amount: number;
    description: string;
    remaining_cents: number;
    order_id: string;
    invoice_id?: string;
    invoice_number?: string;
  } | null;
  orderNumber: string;
  customerEmail: string;
  organizationMinCents: number;
  onSuccess?: () => void;
}

// Dynamic Stripe promise will be created per payment
// const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function PaymentForm({ 
  clientSecret, 
  publishableKey,
  stripeAccountId,
  amountLabel, 
  onSuccess,
  customerEmail 
}: { 
  clientSecret: string; 
  publishableKey: string;
  stripeAccountId?: string;
  amountLabel: string; 
  onSuccess?: () => void;
  customerEmail?: string;
}) {
  const { t } = useLocalization();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    console.log('Loading Stripe with:', { publishableKey: publishableKey?.substring(0, 20) + '...', stripeAccountId });
    
    if (!publishableKey) {
      setLoadError(t('portal.pay.error.configMissing'));
      setIsLoading(false);
      return;
    }

    // Validate publishable key format
    if (!publishableKey.startsWith('pk_')) {
      setLoadError(t('portal.pay.error.configInvalid'));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    
    try {
      const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : undefined;
      console.log('Stripe options:', stripeOptions);
      const promise = loadStripe(publishableKey, stripeOptions);
      
      // Validate that Stripe loaded successfully
      promise.then((stripe) => {
        if (!stripe) {
          setLoadError(t('portal.pay.error.initFailed'));
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      }).catch((error) => {
        console.error('Stripe loading error:', error);
        setLoadError(t('portal.pay.error.loadFailed'));
        setIsLoading(false);
      });
      
      setStripePromise(promise);
    } catch (error) {
      console.error('Error creating Stripe promise:', error);
      setLoadError(t('portal.pay.error.unavailable'));
      setIsLoading(false);
    }
  }, [publishableKey, stripeAccountId, t]);

  if (loadError) {
    return (
      <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
        <p className="text-destructive font-medium">{t('portal.pay.error.title')}</p>
        <p className="text-sm text-muted-foreground mt-1">{loadError}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="text-sm text-primary hover:underline mt-2"
        >
          {t('portal.pay.error.refresh')}
        </button>
      </div>
    );
  }

  if (isLoading || !stripePromise) {
    return <div className="p-4 text-center text-muted-foreground">{t('portal.pay.loading.form')}</div>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <PaymentFormInner 
        clientSecret={clientSecret} 
        amountLabel={amountLabel} 
        onSuccess={onSuccess}
        customerEmail={customerEmail}
      />
    </Elements>
  );
}

function PaymentFormInner({ 
  clientSecret, 
  amountLabel, 
  onSuccess,
  customerEmail 
}: { 
  clientSecret: string; 
  amountLabel: string; 
  onSuccess?: () => void;
  customerEmail?: string;
}) {
  const { t } = useLocalization();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      toast.error(t('portal.pay.error.notReady'));
      return;
    }

    // Check if PaymentElement is mounted and ready
    const paymentElement = elements.getElement("payment");
    if (!paymentElement) {
      toast.error(t('portal.pay.error.formNotReady'));
      return;
    }
    
    setSubmitting(true);
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: buildAbsoluteUrl('/pay/success'),
        },
        redirect: "if_required",
      });
      
      if (error) {
        if (error.code === 'card_declined') {
          toast.error(t('portal.pay.error.cardDeclined'));
        } else if (error.code === 'incomplete_number' || error.code === 'invalid_number') {
          toast.error(t('portal.pay.error.invalidNumber'));
        } else if (error.code === 'incomplete_expiry' || error.code === 'invalid_expiry') {
          toast.error(t('portal.pay.error.invalidExpiry'));
        } else if (error.code === 'incomplete_cvc' || error.code === 'invalid_cvc') {
          toast.error(t('portal.pay.error.invalidCvc'));
        } else {
          toast.error(error.message || t('portal.pay.error.failed'));
        }
      } else {
        toast.success(t('portal.pay.success'));
        onSuccess?.();
      }
    } catch (e: any) {
      toast.error(e.message || t('portal.pay.error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement 
        options={{ 
          layout: "tabs",
          defaultValues: {
            billingDetails: {
              email: customerEmail || undefined
            }
          }
        }}
        onReady={() => {
          setElementReady(true);
        }}
        onLoadError={(error) => {
          console.error("PaymentElement load error:", error);
          toast.error(t('portal.pay.error.formLoadFailed'));
        }}
      />
      <Button 
        onClick={handleSubmit} 
        disabled={!stripe || !elementReady || submitting} 
        className="w-full"
        size="lg"
        aria-describedby={submitting ? "payment-processing" : (!elementReady ? "payment-loading" : undefined)}
      >
        {submitting ? t('portal.pay.button.processing') : !elementReady ? t('portal.pay.button.loading') : amountLabel}
      </Button>
      {submitting && (
        <span id="payment-processing" className="sr-only">
          {t('portal.pay.sr.processing')}
        </span>
      )}
      {!elementReady && (
        <span id="payment-loading" className="sr-only">
          {t('portal.pay.sr.loading')}
        </span>
      )}
    </div>
  );
}

export function PaymentRequestModal({
  open,
  onOpenChange,
  paymentRequest,
  orderNumber,
  customerEmail,
  organizationMinCents,
  onSuccess
}: PaymentRequestModalProps) {
  const { t } = useLocalization();
  const [amountCents, setAmountCents] = useState(0);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [minAmount, setMinAmount] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);

  // Reset state when modal opens/closes or payment request changes
  useEffect(() => {
    if (!open || !paymentRequest) {
      setClientSecret(null);
      setPublishableKey(null);
      setAmountCents(0);
      setMinAmount(0);
      setMaxAmount(0);
      return;
    }

    // Set initial amount and bounds
    const orgMin = Math.max(organizationMinCents, 1);
    const remaining = paymentRequest.remaining_cents;
    
    setMinAmount(orgMin);
    setMaxAmount(remaining);
    setAmountCents(Math.max(orgMin, Math.min(remaining, orgMin)));
  }, [open, paymentRequest, organizationMinCents]);

  const amountInEuros = useMemo(() => amountCents / 100, [amountCents]);
  const minInEuros = useMemo(() => minAmount / 100, [minAmount]);
  const maxInEuros = useMemo(() => maxAmount / 100, [maxAmount]);

  const amountLabel = useMemo(() => {
    return t('portal.pay.amount.payLabel', { amount: amountInEuros.toFixed(2) });
  }, [amountInEuros, t]);

  const isValidAmount = useMemo(() => {
    return amountCents >= minAmount && amountCents <= maxAmount;
  }, [amountCents, minAmount, maxAmount]);

  const handlePrepareIntent = async () => {
    if (!paymentRequest || !isValidAmount) return;

    setLoading(true);
    try {
      setInitError(null); // Clear any previous errors
      
      const { data, error } = await supabase.functions.invoke("portal-pay-intent", {
        body: {
          paymentRequestId: paymentRequest.id,
          amountCents,
          customerEmail,
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        // Show user-friendly error message instead of technical details
        let userFriendlyMessage = t('portal.pay.intent.unavailable');
        
        if (error.message?.includes('Failed to send a request to the Edge Function')) {
          userFriendlyMessage = t('portal.pay.intent.unavailableShort');
        } else if (error.message?.includes('timeout')) {
          userFriendlyMessage = t('portal.pay.intent.timeout');
        } else if (error.message?.includes('network')) {
          userFriendlyMessage = t('portal.pay.intent.network');
        }
        
        setInitError(userFriendlyMessage);
        return;
      }
      
      // Handle specific error codes from the server
      if (data?.error) {
        if (data.code === 'CONCURRENT_PAYMENT') {
          setInitError(t('portal.pay.intent.concurrent'));
        } else if (data.code === 'ALREADY_PAID') {
          setInitError(t('portal.pay.intent.alreadyPaid'));
        } else if (data.code === 'AMOUNT_TOO_LOW' || data.code === 'AMOUNT_TOO_HIGH') {
          // Update bounds if server returned adjusted values
          if (data.minAmount) setMinAmount(data.minAmount);
          if (data.maxAmount) setMaxAmount(data.maxAmount);
          setInitError(data.error);
        } else {
          setInitError(data.error);
        }
        return;
      }

      if (!data?.clientSecret) {
        console.error('No client secret in response:', data);
        setInitError(t('portal.pay.intent.invalidResponse'));
        return;
      }

      if (!data?.publishableKey) {
        console.error('No publishable key in response:', data);
        setInitError(t('portal.pay.intent.configIncomplete'));
        return;
      }

      setClientSecret(data.clientSecret);
      setPublishableKey(data.publishableKey);
      setStripeAccountId(data.stripeAccountId);
      
      // Update bounds if server returned adjusted values
      if (data.minAmount) setMinAmount(data.minAmount);
      if (data.maxAmount) setMaxAmount(data.maxAmount);
      
      console.log('Payment intent created successfully');
    } catch (e: any) {
      console.error('Payment intent error:', e);
      // Show user-friendly error message
      let userFriendlyMessage = t('portal.pay.intent.setupFailed');
      
      if (e.message?.includes('Failed to send a request to the Edge Function')) {
        userFriendlyMessage = t('portal.pay.intent.unavailableShort');
      } else if (e.message?.includes('timeout')) {
        userFriendlyMessage = t('portal.pay.intent.setupTimeout');
      } else if (e.message?.includes('network')) {
        userFriendlyMessage = t('portal.pay.intent.networkError');
      }
      
      setInitError(userFriendlyMessage);
      toast.error(userFriendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAmount = (type: 'min' | 'half' | 'remaining') => {
    switch (type) {
      case 'min':
        setAmountCents(minAmount);
        break;
      case 'half':
        setAmountCents(Math.round(maxAmount / 2));
        break;
      case 'remaining':
        setAmountCents(maxAmount);
        break;
    }
  };

  if (!paymentRequest) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" role="dialog" aria-labelledby="payment-modal-title">
        <DialogHeader>
          <DialogTitle id="payment-modal-title" className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t('portal.pay.modal.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">{/* Context Information */}
          {/* Context Information */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="text-sm text-muted-foreground">
                <div><strong>{t('portal.pay.context.order')}:</strong> {orderNumber}</div>
                {paymentRequest.invoice_id && (
                  <div><strong>{t('portal.pay.context.invoice')}:</strong> {paymentRequest.invoice_number || paymentRequest.invoice_id}</div>
                )}
                <div><strong>{t('portal.pay.context.request')}:</strong> {paymentRequest.description}</div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-muted-foreground">{t('portal.pay.outstanding')}:</span>
                <Badge variant="destructive">
                  €{(paymentRequest.remaining_cents / 100).toFixed(2)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('portal.pay.minimum')}:</span>
                <span className="text-sm font-medium">
                  €{minInEuros.toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>

          {initError ? (
            /* Error State */
            <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
              <p className="text-destructive font-medium">{t('portal.pay.intent.setupErrorTitle')}</p>
              <p className="text-sm text-muted-foreground mt-1">{initError}</p>
              <Button 
                onClick={handlePrepareIntent} 
                disabled={loading}
                variant="outline" 
                className="w-full mt-3"
              >
                {loading ? t('portal.pay.button.retrying') : t('portal.pay.button.tryAgain')}
              </Button>
            </div>
          ) : !clientSecret ? (
            <>
              {/* Amount Input */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="payment-amount" className="text-sm font-medium">
                    {t('portal.pay.amount.label')}
                  </label>
                  <Input
                    id="payment-amount"
                    type="number"
                    min={minInEuros}
                    max={maxInEuros}
                    step="0.01"
                    value={amountInEuros}
                    onChange={(e) => setAmountCents(Math.round(Number(e.target.value) * 100))}
                    className="mt-1"
                    aria-describedby={!isValidAmount ? "amount-error" : undefined}
                    aria-invalid={!isValidAmount}
                  />
                  {!isValidAmount && (
                    <div id="amount-error" className="flex items-center gap-2 mt-2 text-sm text-destructive" role="alert">
                      <AlertCircle className="w-4 h-4" />
                      {t('portal.pay.amount.minError')} €{minInEuros.toFixed(2)} - {t('portal.pay.amount.maxError')} €{maxInEuros.toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Quick Amount Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    onClick={() => handleQuickAmount('min')}
                    className="text-sm sm:text-xs sm:size-sm min-h-[44px] sm:min-h-[36px]"
                    aria-label={t('portal.pay.quick.minAria', { amount: minInEuros.toFixed(2) })}
                  >
                    {t('portal.pay.quick.min')} (€{minInEuros.toFixed(2)})
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    onClick={() => handleQuickAmount('half')}
                    className="text-sm sm:text-xs sm:size-sm min-h-[44px] sm:min-h-[36px]"
                    aria-label={t('portal.pay.quick.halfAria', { amount: (maxInEuros / 2).toFixed(2) })}
                  >
                    {t('portal.pay.quick.half')} (€{(maxInEuros / 2).toFixed(2)})
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    onClick={() => handleQuickAmount('remaining')}
                    className="text-sm sm:text-xs sm:size-sm min-h-[44px] sm:min-h-[36px]"
                    aria-label={t('portal.pay.quick.remainingAria', { amount: maxInEuros.toFixed(2) })}
                  >
                    {t('portal.pay.quick.remaining')} (€{maxInEuros.toFixed(2)})
                  </Button>
                </div>
              </div>

              {/* Continue Button */}
              <Button 
                onClick={handlePrepareIntent} 
                disabled={loading || !isValidAmount} 
                className="w-full"
                size="lg"
                aria-describedby={!isValidAmount ? "button-disabled-reason" : undefined}
              >
                {loading ? t('portal.pay.button.preparing') : `${t('portal.pay.submit')} - €${amountInEuros.toFixed(2)}`}
              </Button>
              {!isValidAmount && (
                <span id="button-disabled-reason" className="sr-only">
                  {t('portal.pay.sr.disabledReason')}
                </span>
              )}
            </>
          ) : (
            /* Stripe Payment Form */
            clientSecret && publishableKey ? (
              <PaymentForm 
                clientSecret={clientSecret}
                publishableKey={publishableKey}
                stripeAccountId={stripeAccountId}
                amountLabel={amountLabel} 
                onSuccess={onSuccess}
                customerEmail={customerEmail}
              />
            ) : null
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
